import { useEffect, useState } from 'react';
import { fetchApi } from '../../api';

/**
 * Active members of the organization, for the "ответственный" picker.
 *
 * Assignment was complete on the backend (`assignedToId`, the team guards, the
 * agent-scoped queries) and had no control anywhere in the interface. The list
 * is the same for every dialog, so it is fetched once per session and shared;
 * `canAssign` mirrors the server-side rule (owner and admin only), so an agent
 * sees who is responsible without being offered a control that would 403.
 */

export interface Assignee {
  id: number;
  name: string;
  email: string;
  role: string;
}

export interface AssigneesState {
  members: Assignee[];
  canAssign: boolean;
  loading: boolean;
  error: string | null;
}

interface AssigneesPayload {
  members: Assignee[];
  canAssign: boolean;
}

let cached: AssigneesPayload | null = null;
let inflight: Promise<AssigneesPayload> | null = null;

function load(): Promise<AssigneesPayload> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = fetchApi('/conversations/assignees')
      .then((data: any) => {
        cached = {
          members: Array.isArray(data?.members) ? data.members : [],
          canAssign: !!data?.canAssign,
        };
        return cached;
      })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** Drops the shared copy, e.g. after the team changes. */
export function invalidateAssignees() {
  cached = null;
}

export function useAssignees(): AssigneesState {
  const [state, setState] = useState<AssigneesState>(() => ({
    members: cached?.members || [],
    canAssign: cached?.canAssign || false,
    loading: !cached,
    error: null,
  }));

  useEffect(() => {
    if (cached) return;
    let active = true;
    load()
      .then(payload => {
        if (active) setState({ ...payload, loading: false, error: null });
      })
      .catch((error: Error) => {
        if (active) {
          setState({ members: [], canAssign: false, loading: false, error: error.message || 'Не удалось загрузить список сотрудников' });
        }
      });
    return () => { active = false; };
  }, []);

  return state;
}
